import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Accounts from './components/Accounts';
import Emails from './components/Emails';
import Settings from './components/Settings';
import Feedback from './components/Feedback';
import ErrorBoundary from './components/ErrorBoundary';
import API_BASE_URL from './apiConfig';
import './App.css';

/* ── Icons ──────────────────────────────────────────────────────── */
const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
  </svg>
);

const MailLogoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="10" fill="#1a73e8" />
    <path d="M8 16l16 11 16-11V32H8V16z" fill="white" opacity="0.3" />
    <path d="M8 14h32l-16 11L8 14z" fill="white" />
    <path d="M8 14v18h32V14L24 25 8 14z" fill="white" fillOpacity="0.15" />
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const InboxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5v-3h3.56c.69 1.19 1.97 2 3.45 2s2.75-.81 3.45-2H19v3zm0-5h-4.99c0 1.1-.9 2-2.01 2s-2.01-.9-2.01-2H5V5h14v9z" />
  </svg>
);

const AccountsNavIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const SettingsNavIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

const FeedbackNavIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12zM7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z" />
  </svg>
);

/* ── CurrentTaskBox ──────────────────────────────────────────────── */
const STEP_SHORT = {
  categorize:          'Categorize',
  requiresResponse:    'Reply?',
  draftResponse:       'Draft',
  evaluateSufficiency: 'Evaluate',
  extractInsights:     'Insights',
};

function senderShort(fromHeader) {
  if (!fromHeader) return '';
  const m = fromHeader.match(/^([^<]+)</);
  const name = m ? m[1].trim() : fromHeader;
  return name.length > 22 ? name.slice(0, 22) + '…' : name;
}

function CurrentTaskBox({ syncState, onStop }) {
  const {
    phase,
    accountEmail,
    accountIdx,
    totalAccounts,
    currentFrom,
    currentSubject,
    currentSteps,
    totalProcessed,
    totalSkipped,
  } = syncState;

  const stepEntries = Object.entries(currentSteps);
  const isSyncing = phase === 'syncing';

  /* ── Idle chip ── */
  if (phase === 'idle') {
    return (
      <div className="task-anchor">
        <div className="task-chip task-chip--idle">
          <span className="task-chip__dot" />
          <span>No pending task</span>
        </div>
      </div>
    );
  }

  /* ── Done chip (auto-dismisses to idle after 8s) ── */
  if (phase === 'done') {
    const msg = totalProcessed === 0
      ? 'Inbox up to date'
      : `${totalProcessed} new email${totalProcessed !== 1 ? 's' : ''} synced`;
    return (
      <div className="task-anchor">
        <div className="task-chip task-chip--done">
          <span className="task-chip__check">✓</span>
          <span>{msg}</span>
          {totalSkipped > 0 && (
            <span className="task-chip__muted"> · {totalSkipped} skipped</span>
          )}
        </div>
      </div>
    );
  }

  /* ── Active chip + dropdown ── */
  const accountSuffix = totalAccounts > 1 ? ` (${accountIdx + 1}/${totalAccounts})` : '';

  return (
    <div className="task-anchor">
      {/* In-header chip — always 1 row, fixed width */}
      <div className="task-chip task-chip--active">
        <span className="task-chip__spinner" />
        <span className="task-chip__account">
          {accountEmail}{accountSuffix}
        </span>
        <button
          className="task-chip__stop"
          onClick={onStop}
          title="Stop sync"
          aria-label="Stop sync"
        >
          ✕
        </button>
      </div>

      {/* Dropdown panel — absolute, overlays content below header */}
      {isSyncing && (
        <div className="task-dropdown" role="status" aria-live="polite">
          <div className="task-dropdown__head">
            <span className="task-dropdown__label">Syncing</span>
            <span className="task-dropdown__account">{accountEmail}{accountSuffix}</span>
          </div>

          {currentSubject ? (
            <div className="task-dropdown__email">
              {currentFrom && (
                <span className="task-dropdown__from">{senderShort(currentFrom)}</span>
              )}
              <span className="task-dropdown__subject">{currentSubject}</span>
            </div>
          ) : (
            <div className="task-dropdown__waiting">
              {stepEntries.length === 0 ? 'Fetching unread emails…' : 'Waiting for next email…'}
            </div>
          )}

          {stepEntries.length > 0 && (
            <div className="task-dropdown__steps">
              {stepEntries.map(([step, status]) => (
                <span key={step} className={`task-pill task-pill--${status}`}>
                  {status === 'running' && <span className="task-pill__spin" />}
                  {status === 'done' && <span className="task-pill__check">✓</span>}
                  {STEP_SHORT[step] ?? step}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Mobile Bottom Nav ───────────────────────────────────────────── */
function BottomNav() {
  const location = useLocation();
  const isEmailsActive =
    location.pathname === '/' || location.pathname.startsWith('/emails');
  const isAccountsActive = location.pathname.startsWith('/accounts');
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isFeedbackActive = location.pathname.startsWith('/feedback');

  return (
    <nav className="mobile-nav" aria-label="Main navigation">
      <div className="mobile-nav__items">
        <Link
          to="/emails"
          className={`mobile-nav__item${isEmailsActive ? ' mobile-nav__item--active' : ''}`}
        >
          <span className="mobile-nav__item-icon"><InboxIcon /></span>
          <span>Inbox</span>
        </Link>
        <Link
          to="/accounts"
          className={`mobile-nav__item${isAccountsActive ? ' mobile-nav__item--active' : ''}`}
        >
          <span className="mobile-nav__item-icon"><AccountsNavIcon /></span>
          <span>Accounts</span>
        </Link>
        <Link
          to="/feedback"
          className={`mobile-nav__item${isFeedbackActive ? ' mobile-nav__item--active' : ''}`}
        >
          <span className="mobile-nav__item-icon"><FeedbackNavIcon /></span>
          <span>Feedback</span>
        </Link>
        <Link
          to="/settings"
          className={`mobile-nav__item${isSettingsActive ? ' mobile-nav__item--active' : ''}`}
        >
          <span className="mobile-nav__item-icon"><SettingsNavIcon /></span>
          <span>Settings</span>
        </Link>
      </div>
    </nav>
  );
}

/* ── Sidebar ─────────────────────────────────────────────────────── */
function Sidebar({ collapsed }) {
  const location = useLocation();
  const isEmailsActive =
    location.pathname === '/' || location.pathname.startsWith('/emails');
  const isAccountsActive = location.pathname.startsWith('/accounts');
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isFeedbackActive = location.pathname.startsWith('/feedback');

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      <nav className="sidebar-nav">
        <Link
          to="/emails"
          className={`sidebar-link${isEmailsActive ? ' sidebar-link--active' : ''}`}
          title="Inbox"
        >
          <span className="sidebar-link__icon"><InboxIcon /></span>
          <span className="sidebar-link__label">Inbox</span>
        </Link>

        <Link
          to="/accounts"
          className={`sidebar-link${isAccountsActive ? ' sidebar-link--active' : ''}`}
          title="Accounts"
        >
          <span className="sidebar-link__icon"><AccountsNavIcon /></span>
          <span className="sidebar-link__label">Accounts</span>
        </Link>

        <Link
          to="/feedback"
          className={`sidebar-link${isFeedbackActive ? ' sidebar-link--active' : ''}`}
          title="Feedback"
        >
          <span className="sidebar-link__icon"><FeedbackNavIcon /></span>
          <span className="sidebar-link__label">Feedback</span>
        </Link>

        <Link
          to="/settings"
          className={`sidebar-link${isSettingsActive ? ' sidebar-link--active' : ''}`}
          title="Settings"
        >
          <span className="sidebar-link__icon"><SettingsNavIcon /></span>
          <span className="sidebar-link__label">Settings</span>
        </Link>
      </nav>
    </aside>
  );
}

/* ── App ─────────────────────────────────────────────────────────── */
function App() {
  const [user, setUser]               = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [syncState, setSyncState] = useState({
    phase: 'idle',           // 'idle' | 'syncing' | 'done'
    accountEmail: null,
    accountIdx: 0,
    totalAccounts: 0,
    currentFrom: null,
    currentSubject: null,
    currentSteps: {},
    totalProcessed: 0,
    totalSkipped: 0,
  });

  // Ref so the cleanup cancels the running sync without needing it in deps
  const cancelledRef  = useRef(false);
  const currentEsRef  = useRef(null);   // tracks the active EventSource so stop can close it immediately
  const dismissTimer  = useRef(null);

  /* ── Auth check ── */
  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/oidc/me`, { credentials: 'include' })
      .then((res) => {
        if (res.status === 401) {
          window.location.href = '/api/auth/oidc/login';
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setUser(data);
          setAuthChecked(true);
        }
      })
      .catch(() => {
        window.location.href = '/api/auth/oidc/login';
      });
  }, []);

  /* ── Auto-sync on login ── */
  useEffect(() => {
    if (!authChecked) return;

    cancelledRef.current = false;

    const runSync = async () => {
      // Fetch accounts
      let accounts;
      try {
        const res = await fetch(`${API_BASE_URL}/accounts`, { credentials: 'include' });
        accounts = await res.json();
        if (!Array.isArray(accounts) || accounts.length === 0) return;
      } catch {
        return;
      }
      if (cancelledRef.current) return;

      let totalProcessed = 0;
      let totalSkipped = 0;

      for (let i = 0; i < accounts.length; i++) {
        if (cancelledRef.current) break;
        const account = accounts[i];

        setSyncState(s => ({
          ...s,
          phase: 'syncing',
          accountEmail: account.email,
          accountIdx: i,
          totalAccounts: accounts.length,
          currentFrom: null,
          currentSubject: null,
          currentSteps: {},
        }));

        await new Promise((resolve) => {
          const es = new EventSource(
            `${API_BASE_URL}/accounts/${account.id}/fetch-emails-stream`,
            { withCredentials: true }
          );
          currentEsRef.current = es;

          es.onmessage = (e) => {
            if (cancelledRef.current) { es.close(); currentEsRef.current = null; resolve(); return; }

            let event;
            try { event = JSON.parse(e.data); } catch { return; }
            if (event.type === 'ping') return;

            if (event.type === 'email_start') {
              setSyncState(s => ({
                ...s,
                currentFrom: event.from,
                currentSubject: event.subject,
                currentSteps: {},
              }));
            } else if (event.type === 'step') {
              setSyncState(s => ({
                ...s,
                currentSteps: { ...s.currentSteps, [event.step]: event.status },
              }));
            } else if (event.type === 'email_done') {
              setSyncState(s => ({
                ...s,
                currentFrom: null,
                currentSubject: null,
                currentSteps: {},
              }));
            } else if (event.type === 'skip') {
              // counted in 'done' event
            } else if (event.type === 'done') {
              totalProcessed += event.count ?? 0;
              totalSkipped   += event.skipped ?? 0;
              es.close();
              currentEsRef.current = null;
              resolve();
            } else if (event.type === 'error') {
              es.close();
              currentEsRef.current = null;
              resolve();
            }
          };

          es.onerror = () => {
            if (es.readyState !== EventSource.CLOSED) es.close();
            currentEsRef.current = null;
            resolve();
          };
        });
      }

      if (cancelledRef.current) return;

      setSyncState(s => ({
        ...s,
        phase: 'done',
        currentFrom: null,
        currentSubject: null,
        currentSteps: {},
        totalProcessed,
        totalSkipped,
      }));

      // Auto-dismiss to idle after 8 seconds
      dismissTimer.current = setTimeout(() => {
        if (!cancelledRef.current) {
          setSyncState(s => ({ ...s, phase: 'idle' }));
        }
      }, 8000);
    };

    runSync();

    return () => {
      cancelledRef.current = true;
      clearTimeout(dismissTimer.current);
    };
  }, [authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleStopSync() {
    cancelledRef.current = true;
    if (currentEsRef.current) {
      currentEsRef.current.close();
      currentEsRef.current = null;
    }
    clearTimeout(dismissTimer.current);
    setSyncState(s => ({ ...s, phase: 'idle' }));
  }

  function handleLogout() {
    fetch(`${API_BASE_URL}/auth/oidc/logout`, {
      method: 'POST',
      credentials: 'include',
    }).then(() => {
      window.location.href = '/api/auth/oidc/login';
    });
  }

  if (!authChecked) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <MailLogoIcon />
          <span>Mail</span>
        </div>
        <div className="spinner" />
      </div>
    );
  }

  const userInitial = (user.email || 'U')[0].toUpperCase();

  return (
    <Router>
      <div className="app-shell">
        {/* ── Header ── */}
        <header className="app-header">
          <div className="header-left">
            <button
              className="icon-btn menu-btn"
              onClick={() => setSidebarCollapsed((c) => !c)}
              aria-label="Toggle sidebar"
            >
              <MenuIcon />
            </button>
            <div className="header-logo">
              <MailLogoIcon />
              <span className="header-title">Mail</span>
            </div>
          </div>

          <div className="header-center">
            <div className="search-bar">
              <span className="search-icon"><SearchIcon /></span>
              <input
                type="text"
                placeholder="Search mail"
                className="search-input"
                aria-label="Search mail"
              />
            </div>

            <CurrentTaskBox syncState={syncState} onStop={handleStopSync} />
          </div>

          <div className="header-right">
            <div className="user-info">
              <span className="user-email-label">{user.email}</span>
              <button
                className="user-avatar-btn"
                onClick={handleLogout}
                title={`Signed in as ${user.email} · Click to sign out`}
                aria-label="Sign out"
              >
                {userInitial}
              </button>
            </div>
          </div>
        </header>

        {/* ── Body ── */}
        <div className="app-body">
          <Sidebar collapsed={sidebarCollapsed} />
          <main className="app-main">
            <Routes>
              <Route path="/" element={<Navigate to="/emails" replace />} />
              <Route
                path="/emails"
                element={
                  <ErrorBoundary>
                    <Emails />
                  </ErrorBoundary>
                }
              />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </main>
        </div>

        {/* ── Mobile bottom nav (hidden on desktop via CSS) ── */}
        <BottomNav />
      </div>
    </Router>
  );
}

export default App;
